import { endsWith, forEach, get, isEmpty, join, map, startsWith } from './lodash';
import { SITE_ROUTER, URL_PARAMETER } from '../constants';
import { isDingTalkPcUA } from './ua';

/**
 * 跳转链接
 *
 * @param url
 */
export const redirect = (url?: string) => {
    if (url) {
        window.location.href = url;
    }
};

/**
 * 在新页面打开tab
 * 不要在异步操作后调用该函数
 * 适用于在非异步操作后新开标签的场景使用，或者特定场景下需要使用 window.open 特性（如开放平台插件的第三方授权相关逻辑）
 * @param url 目标地址
 * @param target 1、窗口名称，2、a标签的target属性
 * @param features 窗口特性列表
 */
export const openInNewTab = (url?: string, target?: string, features?: string) => {
    if (url) {
        // eslint-disable-next-line @fx-ui/jdy-design/window-open-limited
        return window.open(url, target, features);
    }
    return null;
};

/**
 * 使用手动点击 a 标签的形式来在新标签页打开地址，防止使用 window.open() 导致浏览器拦截弹出式窗口
 * 主要是在异步操作后需要新开标签页的场景下作为 openInNewTab 的平替
 * @see https://project.feishu.cn/b2rl2h/assignment/detail/6191375084
 * @see https://kms.fineres.com/pages/viewpage.action?pageId=7645473
 * @param url 目标地址
 * @param target 指示在何处显示链接的资源
 */
export const openInNewTabWithATag = (url: string, target = '_blank') => {
    if (url) {
        let aHref = url;
        if (!['_self', '_top', '_parent'].includes(target) && isDingTalkPcUA()) {
            aHref = getDingtalkUrl(url);
        }
        const a = document.createElement('a');
        a.href = aHref;
        a.target = target;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
};

/**
 * 获取支持通过钉钉内置浏览器打开页面的链接
 * 钉钉要求所有的链接打开方式都需要改为内置浏览器打开，不得跳转外部第三方浏览器，
 * 就算用户没有在设置里勾选【所有页面都在钉钉打开】，也强制要求
 * @see https://open.dingtalk.com/document/orgapp/applink-structure
 * @see https://project.feishu.cn/b2rl2h/story/detail/5980441785
 * @param originalUrl 原始链接
 */
export const getDingtalkUrl = (originalUrl: string) => {
    return insertURLQuery(originalUrl, [
        {
            key: 'ddtab',
            value: 'true'
        }
    ]);
};

/**
 * 获取 url 中指定参数的值
 *
 * @param {string} paramName 参数名称
 * @param {string} customUrl 自定义url
 * @returns {string} 参数值
 */
export const getUrlParameter = (paramName: string, customUrl?: string) => {
    const url = customUrl?.split('?')[1] || window.location.search.substring(1);
    const params = url.split('&');
    let paramValue: string | undefined = '';
    forEach(params, (item) => {
        const param = item.split('=');
        if (param[0] === paramName) {
            paramValue = param[1];
            return false;
        }
    });
    return decodeURIComponent(paramValue);
};

/**
 * 获取 url 中指定参数的值 v2
 * 如果参数值包含多层 ?，如 referer_uri=/profile/me?referer_uri=/dashboard，使用 getUrlParameter 返回结果会不符合预期
 *
 * @param {string} paramName 参数名称
 * @returns {string} 参数值
 */
export const getUrlSearchParams = (paramName: string, url = window.location.href) => {
    return new URL(url, window.location.origin).searchParams.get(paramName);
};

/**
 * 根据邀请链接生成成员类型参数，未指定链接时读取当前页面
 * @param url 邀请链接
 */
export const getInvitationMemberTypeParams = (url?: string): { member_type?: number } => {
    const memberType = getUrlSearchParams(URL_PARAMETER.MEMBER_TYPE, url);
    return memberType ? { member_type: Number(memberType) } : {};
};

/**
 * 自动补齐url拼接短斜杠重复或者没有
 * @param preUrl
 * @param router
 */
export const formatUrlSuffix = (preUrl = '', router: string) => {
    return `${endsWith(preUrl, '/') ? preUrl.substring(0, preUrl.length - 1) : preUrl}${
        startsWith(router, '/') ? router : `/${router}`
    }`;
};

/**
 * 解析api地址
 *
 * @param pattern
 * @param args
 */
export const getApiUrl = (pattern: string, ...args: (string | number)[]) => {
    if (isEmpty(args)) {
        return pattern;
    }
    const reg = /\/:[a-z_-]+/gi;
    let index = 0;
    return pattern.replace(reg, () => {
        return '/' + args[index++];
    });
};

/**
 * 向URL拆入查询参数
 *
 * @param url 原始链接
 * @param query query 参数
 * @return 返回插入 query 参数后的新链接
 */
export const insertURLQuery = (url: string, query: { key: string; value?: string | number }[]) => {
    if (isEmpty(query)) {
        return url;
    }
    const params = join(
        map(query, (item) => {
            return `${item.key}=${item.value}`;
        }),
        '&'
    );

    let prefixUrl = url;
    let hashUrl = '';
    const hashIndex = url.indexOf('#');
    if (hashIndex !== -1) {
        hashUrl = url.slice(hashIndex);
        prefixUrl = url.slice(0, hashIndex);
    }
    prefixUrl += (prefixUrl.indexOf('?') === -1 ? '?' : '&') + params;
    return prefixUrl + hashUrl;
};

/**
 * 从 URL 中移除指定查询参数
 *
 * @param url 原始链接
 * @param keys 待移除的查询参数名
 */
export const removeURLQuery = (url: string, keys: readonly string[]) => {
    const parsedUrl = new URL(url, window.location.origin);
    keys.forEach((key) => parsedUrl.searchParams.delete(key));
    return parsedUrl.toString();
};

/**
 * 判断 URL 是否为有效的绝对路径
 *
 * @param url
 */
export const isValidAbsoluteUrl = (url: string) => {
    /**
     * ((https?:)?\/\/) 协议
     * ([^\/]+) 域名
     * (\/.*)? path
     */
    const regex = /^((https?:)?\/\/)([^\/]+)(\/.*)?$/;
    return regex.test(url);
};

/**
 * 获取切换企业后的跳转地址
 * @param overrideRedirectUri 自定义跳转地址
 */
export const getSwitchCorpRedirectUri = (overrideRedirectUri?: string) => {
    const redirectUri = overrideRedirectUri || getUrlParameter(URL_PARAMETER.REDIRECT_URI);
    return encodeURIComponent(redirectUri || window.location.href);
};

/**
 * 解析公开邀请链接（兼容新旧路由）
 * SITE_ROUTER.CORP_JOIN
 * @param inviteLink
 */
export const parseInviteLink = (
    inviteLink: string
): {
    isMatched: boolean;
    corpId: string;
} => {
    const matchResult = inviteLink.match(/\/corp_join\/([A-Za-z0-9]{24})/);
    if (matchResult) {
        return {
            isMatched: true,
            corpId: get(matchResult, 1) || ''
        };
    }
    const path = inviteLink.split('?')[0] || '';
    const isPathValid = path.match(new RegExp(SITE_ROUTER.CORP_JOIN));
    const corpId = getUrlParameter('corp_id', inviteLink);
    const isCorpIdValid = corpId.match(/^[A-Za-z0-9]{24}$/);
    if (isPathValid && isCorpIdValid) {
        return { isMatched: true, corpId };
    }
    return { isMatched: false, corpId: '' };
};
